import { createAsyncThunk } from '@reduxjs/toolkit'
import { UseFormReturn } from 'react-hook-form/dist/types/form'
import { get, mapKeys, snakeCase } from 'lodash'
import qs from 'query-string'

import authRequest, {
  Credentials,
  MailResetPassType,
  ResetPasswordType,
  SettingPasswordType,
} from 'src/api/requests/auth'
import { FormValues, ParamsType } from 'src/containers/Auth/SignIn/useLogin'
import { HTTP_CODE } from 'src/constants/app'
import LocalStorage from 'src/utils/LocalStorage'
import history from 'src/libs/history'
// import { PATH as FLOW_PATH } from 'src/pages/homeFlow'
import { PATH as AUTH_PATH } from 'src/pages/auth'
import { PATH as MY_PAGE_PATH } from 'src/pages/myPage'
import uiActions from 'src/store/ui/notification'
import appActions from '../store/ui/app'
import notificationAction from 'src/store/domain/notification/countUnreadNotification'
import userRequest from '../api/requests/user'
import TagManager from 'react-gtm-module'
import { handleRedirectByState } from './keepFlow'

export interface PostLogin {
  credentials: Credentials
  meta: UseFormReturn<FormValues, any>
  params: ParamsType
}

export const login = createAsyncThunk<any, PostLogin>(
  'login',
  async ({ credentials, meta, params }, { dispatch }) => {
    try {
      const res = await authRequest.login(credentials)
      const token = get(res, 'data.access_token')

      LocalStorage.setToken(token)

      // set gg tag id
      const gtmId = get(res, 'data.currentUser.clinics[0].gtm_id', '')
      const currentGmtId = LocalStorage.getGtmId()
      LocalStorage.setGtmId(gtmId)
      if (currentGmtId !== gtmId) {
        TagManager.initialize({
          gtmId: gtmId,
        })
      }

      // const hasSubscription = userInfo && userInfo.cycleOrderPaid
      // [SCOPE] No.154 (FB1) — added `userTreatments.lastDoctorFeedback` so
      // matchedTreatment.last_doctor_feedback.id is populated right after
      // login. Without it, handleRedirectByState's CONFIRMING_PRESCRIPTION
      // branch always saw a missing id and fell back to the blank
      // CHOOSE_SERVICE_PLAN screen instead of PRESCRIPTION_PROPOSAL/:id.
      const resUserInfo = await userRequest.userInfo({
        with: 'reservations,card,stateTransitions,cycleOrderPaid,latestOrderCanPay,userTreatments,userTreatments.reservations,userTreatments.lastDoctorFeedback',
      })
      dispatch(appActions.updateAuthInfo(resUserInfo.data))
      dispatch(
        notificationAction.updateEntity(resUserInfo.data?.notice_unread_count),
      )
      const redirectPage =
        params.redirect && atob(params.redirect)
          ? atob(params.redirect)
          : MY_PAGE_PATH.OTHER_MENU

      let queryParams =
        redirectPage.split('?').length > 1
          ? qs.parse(redirectPage.split('?')[1])
          : {}
      const pathName = redirectPage.split('?')[0]

      // [SCOPE] Feedback 3: extract menuId from the redirect URL (from the
      // path segment or query param) BEFORE mapKeys converts it to menu_id,
      // then find the user_treatment and navigate to the correct screen based
      // on treatment state rather than pushing the raw redirect URL directly.
      const redirectMenuId =
        (queryParams.menuId as string | undefined) ||
        (queryParams.menu_id as string | undefined) ||
        pathName.split('/').pop()

      const userTreatments: any[] = resUserInfo.data?.user_treatments || []
      const matchedTreatment = redirectMenuId
        ? userTreatments.find(
            (t: any) => String(t.menu_id) === String(redirectMenuId),
          )
        : null

      if (matchedTreatment) {
        LocalStorage.removeTelemedicine()
        const targetPath = handleRedirectByState(
          matchedTreatment,
          resUserInfo.data,
          queryParams,
        )
        if (targetPath && targetPath !== 'popup') {
          return history.push(targetPath)
        }
      }

      queryParams = mapKeys(queryParams, (value, key) => snakeCase(key))

      return history.push(
        `${pathName}${queryParams ? `?${qs.stringify(queryParams)}` : ''}`,
      )
    } catch (err: any) {
      const status = err.response.status
      if (status === HTTP_CODE.BAD_REQUEST) {
        // meta.setError('email', { type: 'validate' })
        // meta.setError('password', { type: 'validate' })
        meta.setValue('password', '')
        dispatch(
          uiActions.onNotification({
            type: 'error',
            message: 'メールアドレスまたはパスワードが正しくありません。',
          }),
        )
      }
    }
  },
)

export interface PostSendMailRessetPassword {
  mailResetPassType: MailResetPassType
  form: UseFormReturn<FormValues, any>
}

export const sendMailResetPassword = createAsyncThunk<
  any,
  PostSendMailRessetPassword
>(
  'send_mail_reset_password',
  async ({ mailResetPassType, form }, { dispatch }) => {
    try {
      const res = await authRequest.sendMailResetPassword(mailResetPassType)
      dispatch(
        uiActions.onNotification({
          type: 'success',
          message: res.data.message,
        }),
      )
    } catch (err: any) {
      const status = err.response.status
      if (status === HTTP_CODE.BAD_REQUEST) {
        dispatch(
          uiActions.onNotification({
            type: 'error',
            message: 'メールアドレスが正しくありません。',
          }),
        )
      }
    }
  },
)
export interface PostResetPassword {
  resetPassword: ResetPasswordType
  form: UseFormReturn<ResetPasswordType, any>
}

export const resetPassword = createAsyncThunk<any, PostResetPassword>(
  'reset_password',
  async ({ resetPassword, form }, { dispatch }) => {
    try {
      await authRequest.resetPassword(resetPassword)
      history.push(AUTH_PATH.COMPLETE_PAGE)
    } catch (err: any) {
      dispatch(
        uiActions.onNotification({
          type: 'error',
          message: err.response.data.message,
        }),
      )
    }
  },
)

export interface DataLinkLine {
  data: {
    code: string
    url?: string
  }
}

export const lintLine = createAsyncThunk<any, DataLinkLine>(
  'link-line',
  async ({ data }, { dispatch }) => {
    try {
      await authRequest.linkLine(data)
      LocalStorage.removePathBeforeLoginLine()
      const resUserInfo = await userRequest.userInfo({
        with: 'reservations,card,stateTransitions,cycleOrderPaid,latestOrderCanPay,orderPaymented,userTreatments,userTreatments.reservations',
      })
      dispatch(appActions.updateAuthInfo(resUserInfo.data))
      history.push(MY_PAGE_PATH.OTHER_MENU)

      dispatch(
        uiActions.onNotification({
          type: 'success',
          message: 'LINEに連携しました。',
        }),
      )
    } catch (err: any) {
      const beforeUrl = LocalStorage.getPathBeforeLoginLine()
      LocalStorage.removePathBeforeLoginLine()
      if (beforeUrl) {
        LocalStorage.removePathBeforeLoginLine()
        history.push(beforeUrl)
      } else {
        history.push(MY_PAGE_PATH.OTHER_MENU)
      }
      dispatch(
        uiActions.onNotification({
          type: 'error',
          message: err.response.data.message,
        }),
      )
    }
  },
)

export interface PostSendMailSettingPassword {
  mailSettingPassType: MailResetPassType
  form: UseFormReturn<FormValues, any>
}

export const sendMailSettingPassword = createAsyncThunk<
  any,
  PostSendMailSettingPassword
>(
  'send_mail_setting_password',
  async ({ mailSettingPassType }, { dispatch }) => {
    try {
      const res = await authRequest.sendMailSettingPassword(mailSettingPassType)
      dispatch(
        uiActions.onNotification({
          type: 'success',
          message: res.data.message,
        }),
      )
    } catch (err: any) {
      const status = err.response.status
      if (status === HTTP_CODE.BAD_REQUEST) {
        const message =
          err.response.data?.message || 'メールアドレスが正しくありません。'
        dispatch(
          uiActions.onNotification({
            type: 'error',
            message,
          }),
        )
      }
    }
  },
)

export interface PostSettingPassword {
  settingPassword: SettingPasswordType
  form: UseFormReturn<SettingPasswordType, any>
}

export const settingPassword = createAsyncThunk<any, PostSettingPassword>(
  'setting_password',
  async ({ settingPassword }, { dispatch }) => {
    try {
      await authRequest.settingPassword(settingPassword)
      history.push(AUTH_PATH.SIGN_IN)
    } catch (err: any) {
      dispatch(
        uiActions.onNotification({
          type: 'error',
          message: err.response.data.message,
        }),
      )
    }
  },
)
